import React from 'react';
import {
  Paper,
  Box,
  Text,
  Group,
  ScrollArea,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  FileButton,
  Button,
  LoadingOverlay,
} from '@mantine/core';
import {
  IconFolder,
  IconFolderOpen,
  IconFile,
  IconFileCode,
  IconPhoto,
  IconMusic,
  IconTrash,
  IconUpload,
  IconDownload,
  IconFilePlus,
  IconPencil,
  IconCopy,
  IconCheck,
} from '@tabler/icons-react';
import { isTextFileExtension, isHtmlFile, isSvgFile, isImageFile, isAudioFile } from '@/Components/MonacoCodeEditor';
import { useFileExplorerContext } from './FileExplorerContext';
import { AutoResetFileButton } from '@/Components/AutoResetFileButton';

export function getExplorerFileIcon(filePath: string) {
  if (isImageFile(filePath) || isSvgFile(filePath)) return <IconPhoto size={16} color="var(--mantine-color-teal-6)" />;
  if (isHtmlFile(filePath) || isTextFileExtension(filePath)) return <IconFileCode size={16} color="var(--mantine-color-blue-6)" />;
  if (isAudioFile(filePath)) return <IconMusic size={16} color="var(--mantine-color-violet-6)" />;
  return <IconFile size={16} color="gray" />;
}

export function FolderContents() {
  const {
    mode,
    selectedFolder,
    setSelectedFolder,
    selectedFile,
    setSelectedFile,
    setSearchQuery,
    currentViewItems,
    adapter,
    isUploading,
    isDraggingOver,
    onUploadFiles,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    openActionModal,
    onDeleteFolder,
    onDeleteFile,
    onConfirmPick,
  } = useFileExplorerContext();

  return (
    <Paper
      withBorder
      p="xs"
      radius="md"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e)}
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderColor: isDraggingOver ? 'var(--mantine-color-blue-5)' : undefined,
        borderWidth: isDraggingOver ? '2px' : undefined,
        borderStyle: isDraggingOver ? 'dashed' : undefined,
        backgroundColor: isDraggingOver ? 'var(--mantine-color-blue-0)' : undefined,
        transition: 'all 0.15s ease',
      }}
    >
      <LoadingOverlay visible={isUploading} overlayProps={{ radius: 'md', blur: 2 }} />

      {isDraggingOver && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 50,
            backgroundColor: 'rgba(235, 245, 255, 0.94)',
            borderRadius: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <IconUpload size={44} color="var(--mantine-color-blue-6)" style={{ marginBottom: 8 }} />
          <Text fw={600} size="sm" c="blue.7">
            放開以直接上傳檔案或資料夾
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            目標目錄：{selectedFolder ? `${selectedFolder}/` : '根目錄 /'}
          </Text>
        </Box>
      )}

      <Group justify="space-between" mb="xs" px="xs">
        <Text size="xs" fw={600} c="dimmed">
          {currentViewItems.isSearchMode
            ? `搜尋結果 (${currentViewItems.files.length})`
            : `目錄內容: ${selectedFolder || '根目錄'} (${currentViewItems.subfolders.length} 資料夾, ${currentViewItems.files.length} 檔案)`}
        </Text>
        {mode === 'pick' && (
          <Text size="xs" c="dimmed">
            提示：雙擊合法項目可直接確認選取
          </Text>
        )}
      </Group>

      <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
        {currentViewItems.subfolders.length === 0 && currentViewItems.files.length === 0 ? (
          <Box py="xl" style={{ textAlign: 'center' }}>
            <IconFolderOpen size={36} color="gray" style={{ margin: '0 auto 8px', opacity: 0.7 }} />
            <Text size="sm" fw={500} c="dimmed">
              此資料夾為空
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              支援直接拖放檔案或整個資料夾至此上傳
            </Text>
            <Group justify="center" mt="md" gap="xs">
              <Button
                variant="light"
                color="teal"
                size="xs"
                leftSection={<IconFilePlus size={14} />}
                onClick={() => openActionModal({ type: 'createFile', targetFolder: selectedFolder })}
              >
                新增文字檔案
              </Button>
              <FileButton
                onChange={(files) => onUploadFiles(files ? Array.from(files) : null)}
                multiple
              >
                {(props) => (
                  <Button {...props} variant="light" size="xs" leftSection={<IconUpload size={14} />}>
                    選擇檔案上傳
                  </Button>
                )}
              </FileButton>
            </Group>
          </Box>
        ) : (
          <Table verticalSpacing={6} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>名稱</Table.Th>
                <Table.Th style={{ width: mode === 'pick' ? '120px' : '150px', textAlign: 'right' }}>操作</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* Subfolders in current view */}
              {currentViewItems.subfolders.map((folderPath) => {
                const folderName = folderPath.split('/').pop();
                return (
                  <Table.Tr
                    key={`dir-${folderPath}`}
                    onClick={() => {
                      setSelectedFolder(folderPath);
                      setSearchQuery('');
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => onDrop(e, folderPath)}
                    style={{ cursor: 'pointer', background: 'var(--mantine-color-gray-0)' }}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        <IconFolder size={18} color="var(--mantine-color-yellow-6)" />
                        <Text size="xs" fw={600}>
                          {folderName}/
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Group gap={2} justify="flex-end">
                        <Tooltip label="重新命名資料夾">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="orange"
                            onClick={(e) => {
                              e.stopPropagation();
                              openActionModal({ type: 'rename', itemType: 'folder', currentPath: folderPath });
                            }}
                          >
                            <IconPencil size={14} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="建立資料夾副本">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="cyan"
                            onClick={(e) => {
                              e.stopPropagation();
                              openActionModal({ type: 'duplicate', itemType: 'folder', currentPath: folderPath });
                            }}
                          >
                            <IconCopy size={14} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="刪除此資料夾">
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteFolder(folderPath, e);
                            }}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}

              {/* Files in current view */}
              {currentViewItems.files.map((item) => {
                const isSelected = selectedFile === item.fullPath;
                const isAllowed = item.isAllowed !== false;
                const isManifest =
                  item.fullPath.toLowerCase() === 'extension.json' ||
                  item.fullPath.toLowerCase() === 'manifest.json';

                return (
                  <Table.Tr
                    key={`file-${item.fullPath}`}
                    onClick={() => {
                      if (!isAllowed) return;
                      setSelectedFile(item.fullPath);
                    }}
                    onDoubleClick={() => {
                      if (mode === 'pick' && isAllowed) {
                        onConfirmPick(item.fullPath);
                      }
                    }}
                    style={{
                      cursor: isAllowed ? 'pointer' : 'not-allowed',
                      opacity: isAllowed ? 1 : 0.45,
                      background: isSelected ? 'var(--mantine-color-blue-light)' : undefined,
                    }}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        {getExplorerFileIcon(item.fullPath)}
                        <Text
                          size="xs"
                          fw={isSelected ? 600 : 400}
                          style={{ fontFamily: 'monospace' }}
                        >
                          {item.name}
                        </Text>
                        {isManifest && (
                          <Badge color="green" size="xs" variant="light">
                            Manifest
                          </Badge>
                        )}
                        {!isAllowed && (
                          <Badge color="gray" size="xs" variant="outline">
                            格式不符
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Group gap={2} justify="flex-end">
                        {mode === 'pick' ? (
                          <Button
                            size="compact-xs"
                            variant={isSelected ? 'filled' : 'light'}
                            color="blue"
                            disabled={!isAllowed}
                            leftSection={<IconCheck size={12} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onConfirmPick(item.fullPath);
                            }}
                          >
                            選取
                          </Button>
                        ) : (
                          <>
                            <Tooltip label="重新命名檔案">
                              <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="orange"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openActionModal({ type: 'rename', itemType: 'file', currentPath: item.fullPath });
                                }}
                              >
                                <IconPencil size={14} />
                              </ActionIcon>
                            </Tooltip>

                            <Tooltip label="建立檔案副本">
                              <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="cyan"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openActionModal({ type: 'duplicate', itemType: 'file', currentPath: item.fullPath });
                                }}
                              >
                                <IconCopy size={14} />
                              </ActionIcon>
                            </Tooltip>

                            <AutoResetFileButton
                              onChange={(file) => {
                                if (file) onUploadFiles([file], item.fullPath);
                              }}
                            >
                              {(props) => (
                                <Tooltip label="覆蓋檔案">
                                  <ActionIcon
                                    {...props}
                                    size="xs"
                                    variant="subtle"
                                    color="orange"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      props.onClick();
                                    }}
                                  >
                                    <IconUpload size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </AutoResetFileButton>

                            <Tooltip label="下載檔案">
                              <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="teal"
                                component="a"
                                href={adapter.getFileUrl(item.fullPath)}
                                target="_blank"
                                download={item.name}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconDownload size={14} />
                              </ActionIcon>
                            </Tooltip>

                            <Tooltip label="刪除檔案">
                              <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteFile(item.fullPath, e);
                                }}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </ScrollArea>
    </Paper>
  );
}
